<template>
  <v-container fluid class="fill-height">
    <v-row class="fill-height">
      <!-- Left Column -->
      <v-col cols="6" class="d-flex flex-column fill-height">
        <!-- Search (fixed) -->
        <div class="flex-shrink-0 pa-3">
          <v-text-field
            v-model="searchInput"
            solo
            clearable
            label="Search Name"
            messages="Start typing"
            prepend-inner-icon="mdi-magnify"
          >
            <template v-slot:append-outer>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    :disabled="open.length === 0"
                    @click="collapseAll"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon>mdi-collapse-all</v-icon>
                  </v-btn>
                </template>
                <span>Collapse all</span>
              </v-tooltip>
            </template>
          </v-text-field>
        </div>

        <!-- Tree view (reactive) -->
        <div
          ref="treeScroll"
          class="flex-grow-1"
          style="overflow-y: auto; min-height: 0"
        >
          <!-- Every rebuild blocks the main thread for seconds at
               production sizes, and later ones happen with a tree already on
               screen — frozen, with nothing to say why. The cache announces
               the run one paint before it starts so this can be shown. -->
          <v-progress-linear
            v-if="!lazy && $wob.tree.isRebuilding && $wob.tree.hasEverBuilt"
            indeterminate
            color="secondary"
            height="2"
          />

          <!-- Shown in that same early paint, but only before the first tree
               exists: replacing a built tree with a placeholder would throw
               away scroll position and expanded state. -->
          <div
            v-if="!(lazy ? topLoaded : $wob.tree.hasEverBuilt)"
            class="d-flex flex-column align-center justify-center pa-8 grey--text"
          >
            <v-progress-circular indeterminate color="secondary" class="mb-3" />
            <span class="text-caption">Building tree…</span>
          </div>

          <!-- Searching filters the tree itself: the branches that lead to
               a match, opened. Too many matches to draw are not drawn at
               all — the tree stays as it was and says how many there are. -->
          <v-alert
            v-if="lazy && tooMany"
            dense
            text
            type="info"
            class="mx-3 mb-2"
          >
            {{ tooMany }} matches — keep typing
          </v-alert>
          <v-alert
            v-else-if="lazy && search && !items.length"
            dense
            text
            type="info"
            class="mx-3 mb-2"
          >
            Nothing matches “{{ search }}”
          </v-alert>

          <v-treeview
            v-else
            :key="treeKey"
            activatable
            :search="lazy ? null : search"
            :items="lazy ? items : $wob.tree.nodes"
            :load-children="lazy ? loadChildren : undefined"
            @update:active="onActive"
            :active.sync="active"
            :open.sync="open"
          >
            <template v-slot:prepend="{ item }">
              <span class="text-caption d-flex align-center">
                <v-badge
                  v-if="countOf(item) > 0"
                  :content="countOf(item)"
                  overlap
                  color="accent"
                  class="mr-4"
                >
                  <v-icon color="secondary">{{
                    recTypeIcon(item.type)
                  }}</v-icon>
                </v-badge>
                <v-icon v-else color="secondary" class="mr-2">{{
                  recTypeIcon(item.type)
                }}</v-icon>
                {{ item.type }}
              </span>
            </template>
          </v-treeview>
        </div>
      </v-col>

      <!-- Right Column -->
      <v-col cols="6" class="d-flex flex-column fill-height">
        <div class="flex-grow-1" style="overflow-y: auto; min-height: 0">
          <div v-if="!activeId" class="pa-3">
            <v-alert color="secondary" type="info"> The view is empty </v-alert>
          </div>

          <individual-expanded v-if="activeId" :individual="individual" link />
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { recIcon } from '@/vue-wob-cache/vue-wob-icons'
import IndividualExpanded from '@/components/IndividualExpanded.vue'
import { observes } from '@/vue-wob-cache/vue-wob-observes'

import debounce from 'debounce'

// v-treeview re-filters on every change to :search, and each filter walks every
// node in the tree — ~37k on the Fabege graph — lowercasing both the node text
// and the search string for each. Bound straight to the text field that ran on
// every keystroke, blocking the input itself, so characters appeared late.
// Longer than SearchView's 200ms because each pass here is heavier.
const TREE_SEARCH_DEBOUNCE_MS = 300

// Matches the tree will filter down to. Finding them costs milliseconds
// whatever the graph (Fabege: 3-14 ms for 50,664 nodes), and building their
// branches 2-5 ms for 500; what costs is drawing the rows, so this is a
// limit on rows, not on work. Above it the tree is left alone: a tree of
// thousands of hits helps nobody. Ids are indexed too, and they are uuids,
// so a single letter matches nearly everything — hence a limit on matches
// rather than on how much has been typed.
const FILTER_LIMIT = 200

export default {
  name: 'TreeView',

  // On 0.9.x the cache keeps a whole tree built while this view is mounted,
  // and stops once it is gone. The next engine opens the tree level by
  // level, and its observe() builds nothing.
  mixins: [observes('tree')],

  components: {
    IndividualExpanded,
  },

  data: () => ({
    activeId: null,
    individual: null,
    // What the text field shows, updated on every keystroke.
    searchInput: null,
    // What v-treeview filters on, applied once typing pauses.
    search: null,
    active: [],
    open: [],
    // Guards against v-treeview .sync clearing restored state before items load
    stateRestored: false,
    // Level by level: the top level, then each node's children as it opens.
    items: [],
    topLoaded: false,
    // While searching: how many matched, when that is too many to draw.
    tooMany: 0,
    // What was open before searching, put back when the box is cleared.
    openBeforeSearch: null,
    // Bumped when the items are replaced wholesale, so v-treeview starts
    // again rather than keeping state for nodes that are no longer there.
    treeKey: 0,
  }),

  computed: {
    // The engine that opens the tree level by level rather than building it.
    lazy() {
      return !!this.$wob.tree.lazy
    },
  },

  created() {
    // Per instance, so a pending apply can be cancelled when this view goes.
    this.applySearch = debounce((value) => {
      this.search = value
    }, TREE_SEARCH_DEBOUNCE_MS)
  },

  beforeDestroy() {
    this.applySearch.clear()
  },

  mounted() {
    // Restore search immediately (not affected by tree items)
    this.search = this.$store.getters.getTreeSearch
    this.searchInput = this.search

    if (this.lazy) {
      this.loadTop()
      if (this.search) this.runSearch(this.search)
      return
    }
    // If tree items are already available, restore state now
    if (this.$wob.tree.nodes && this.$wob.tree.nodes.length > 0) {
      this._restoreTreeState()
    }
    // Otherwise the watcher on $wob.tree.nodes will restore when items arrive
  },

  watch: {
    'searchInput'(value) {
      // Clearing should take effect at once; only typing waits for a pause.
      if (!value) {
        this.applySearch.clear()
        this.search = value
        return
      }
      this.applySearch(value)
    },

    '$wob.tree.nodes'(nodes) {
      if (this.lazy) return
      if (!this.stateRestored && nodes && nodes.length > 0) {
        this._restoreTreeState()
      }
    },

    // A sync changed something: reload the levels on screen, leaving them
    // open where they still exist.
    '$wob.revision'() {
      if (!this.lazy) return
      this.loadTop()
      if (this.search) this.runSearch(this.search)
    },

    'search'(value) {
      if (this.lazy) this.runSearch(value)
    },
  },

  beforeRouteLeave(to, from, next) {
    // Save state to Vuex store before leaving
    this.$store.commit('setTreeActiveId', this.activeId)
    this.$store.commit('setTreeActive', this.active)
    this.$store.commit('setTreeOpen', this.open)
    // What the user typed, even if the last keystrokes are still debouncing.
    this.$store.commit('setTreeSearch', this.searchInput)
    next()
  },

  methods: {
    /**
     * How many children to show on the badge. The engine counts them to
     * answer whether a node has any, so the number is known before they
     * are loaded; on 0.9.x only the loaded ones are.
     */
    countOf(item) {
      return item.childCount ?? item.children?.length ?? 0
    },

    /** The top level: groups, and the roots alone of their type. */
    loadTop() {
      const { nodes } = this.$wob.tree.top()
      // Restore first: it decides which branches are open, and replacing
      // the items publishes them.
      if (!this.stateRestored) this._restoreTreeState()
      const open = [...this.open]
      this.replaceItems(
        nodes.map((node) => this.asItem(node, new Set(open))),
        open
      )
    },

    /**
     * A tree item. `children: []` is what makes v-treeview offer the
     * expander and ask load-children when it is opened; a level already
     * open is loaded again, so it stays as it was.
     */
    asItem(node, open) {
      const item = {
        id: node.id,
        name: node.name,
        type: node.type,
        childCount: node.childCount ?? 0,
      }
      const hasChildren = node.hasChildren || node.childCount > 0
      if (!hasChildren) return item
      item.children = open && open.has(node.id) ? this.childrenOf(item) : []
      return item
    },

    /** v-treeview calls this the first time a node is opened. */
    async loadChildren(item) {
      item.children.push(...this.childrenOf(item))
    },

    childrenOf(item) {
      const path = item.id.startsWith('group:')
        ? []
        : this.$wob.tree.pathTo(item.id).slice(0, -1)
      const { nodes } = this.$wob.tree.children(item.id, path)
      const open = new Set(this.open)
      return nodes.map((node) => this.asItem(node, open))
    },

    /**
     * Search the whole graph — not only what has been expanded — and show
     * the branches that lead to a match, opened.
     */
    runSearch(text) {
      if (!text) {
        this.tooMany = 0
        // Back to the tree, opened where it was before the search.
        this.open = this.openBeforeSearch ?? []
        this.openBeforeSearch = null
        this.loadTop()
        return
      }
      if (
        this.openBeforeSearch === null ||
        this.openBeforeSearch === undefined
      ) {
        this.openBeforeSearch = this.open
      }
      const { ids, total } = this.$wob.index.find(text, {
        limit: FILTER_LIMIT,
      })
      if (total > FILTER_LIMIT) {
        this.tooMany = total
        return
      }
      this.tooMany = 0
      this.showBranchesTo(ids)
    },

    /**
     * The branches leading to the hits, from the engine, opened all the way
     * down. A hit with children of its own keeps its expander, so exploring
     * carries on from there.
     */
    showBranchesTo(ids) {
      const open = []
      const asBranch = (node) => {
        const item = {
          id: node.id,
          name: node.name,
          type: node.type,
          childCount: node.childCount ?? 0,
        }
        if (node.children) {
          open.push(node.id)
          item.children = node.children.map(asBranch)
        } else if (node.hasChildren) {
          item.children = []
        }
        return item
      }
      this.replaceItems(this.$wob.tree.branchesTo(ids).map(asBranch), open)
    },

    /**
     * Replaces what the tree shows, and says which branches are open.
     *
     * v-treeview re-syncs :open against the items it holds, so setting both
     * in one tick loses the open branches: it answers with the ones it knew
     * of, which is none of the new ones. Hence the new items first, then the
     * open branches once it has them.
     */
    replaceItems(items, open) {
      this.items = items
      this.treeKey++
      this.topLoaded = true
      this.$nextTick(() => {
        this.open = open
      })
    },

    collapseAll() {
      this.open = []
      this.active = []
      this.activeId = null
      this.individual = null
    },

    /**
     * Get icon for REC type.
     * @param {string} type - Type string (e.g., "Building", "RealEstate")
     * @returns {string} Material Design Icon name
     */
    recTypeIcon(type) {
      return recIcon(type)
    },

    /**
     * Restore tree expand/active state from Vuex store.
     * Validates all IDs against the current tree — handles pruning,
     * moved nodes, and removed leaves gracefully.
     */
    _restoreTreeState() {
      this.stateRestored = true
      const storedOpen = this.$store.getters.getTreeOpen || []
      const storedActiveId = this.$store.getters.getTreeActiveId

      // Filter open IDs to only those still present in the tree
      // (group: nodes are virtual parents — keep them if they still exist)
      const exists = (id) =>
        this.lazy
          ? this.$wob.tree.nodeOf(id) != null
          : this.$wob.tree.findNode(id) != null
      const validOpen = storedOpen.filter((id) => {
        return id.startsWith('group:') || exists(id)
      })

      if (validOpen.length > 0) {
        this.open = validOpen
      }

      // Validate the active/focused node
      if (storedActiveId && exists(storedActiveId)) {
        // Node still exists — ensure its full path is open so it's visible.
        // Handles the case where a node moved to a different branch.
        const path = this.lazy
          ? this.$wob.tree.pathTo(storedActiveId)
          : this.$wob.tree.getPath(storedActiveId)
        // Open all ancestors (all IDs except the leaf itself)
        const ancestorIds = path.slice(0, -1)
        const openSet = new Set(this.open)
        for (const id of ancestorIds) {
          openSet.add(id)
        }
        this.open = [...openSet]

        this.active = [storedActiveId]
        this.activeId = storedActiveId
        this.individual = this.$wob.read(storedActiveId)
        this._scrollToRestoredNode()
      } else if (validOpen.length > 0) {
        // No focused node but tree is open — scroll to first open node
        this._scrollToFirstOpenNode()
      } else {
        // Focused node was removed — clear selection
        this.active = []
        this.activeId = null
        this.individual = null
      }
    },

    /**
     * Scroll the active node into view after DOM updates.
     */
    _scrollToRestoredNode() {
      this.$nextTick(() => {
        const container = this.$refs.treeScroll
        if (!container) return
        // Vuetify marks active nodes with this class
        const el = container.querySelector('.v-treeview-node--active')
        if (el) {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' })
        }
      })
    },

    /**
     * Scroll the first open node into view.
     * Uses data-id attribute rendered by v-treeview to locate the node.
     */
    _scrollToFirstOpenNode() {
      this.$nextTick(() => {
        const container = this.$refs.treeScroll
        if (!container) return
        // v-treeview expands nodes with the v-treeview-node--open class;
        // find the first one in DOM order (topmost expanded branch)
        const el = container.querySelector('.v-treeview-node--open')
        if (el) {
          el.scrollIntoView({ block: 'start', behavior: 'smooth' })
        }
      })
    },

    /**
     * Handle tree node selection.
     * @param {Array<string>} ids - Array of selected node IDs
     */
    onActive(ids) {
      const [selected] = ids

      // Ignore group node selections
      if (selected && !selected.startsWith('group:')) {
        this.active = [selected]
        this.activeId = selected
        this.individual = this.$wob.read(selected)
      } else {
        this.active = []
        this.activeId = null
        this.individual = null
      }
    },
  },
}
</script>
